/**
 * TaxSetu AI - Pricing Page
 * Pro subscription with Razorpay integration
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Check, 
  Zap, 
  Crown, 
  ArrowLeft, 
  Sparkles,
  MessageSquare,
  FileText,
  Clock,
  Shield,
  Star,
  IndianRupee
} from 'lucide-react';
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import ThemeToggle from "../components/ThemeToggle";
import { toast } from 'sonner';
import { API_BASE_URL } from "../utils/api";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro_monthly');
  const [subscription, setSubscription] = useState(null);

  // Fetch current subscription status
  useEffect(() => {
    if (user?.email) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/payment/subscription/${encodeURIComponent(user.email)}`);
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (planId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      navigate('/chat');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        setLoading(false);
        return;
      }

      // Create order
      const orderRes = await fetch(`${API_BASE_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: planId,
          user_email: user.email,
          user_name: user.name
        })
      });

      if (!orderRes.ok) {
        const error = await orderRes.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await orderRes.json();

      // Open Razorpay checkout
      const options = {
        key: RAZORPAY_KEY,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'TaxSetu AI',
        description: orderData.plan_name,
        order_id: orderData.order_id,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyRes = await fetch(`${API_BASE_URL}/api/payment/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                user_email: user.email,
                plan_id: planId
              })
            });

            if (verifyRes.ok) {
              toast.success('🎉 Welcome to TaxSetu Pro!');
              fetchSubscription();
              setTimeout(() => navigate('/chat'), 2000);
            } else {
              toast.error('Payment verification failed');
            }
          } catch (err) {
            toast.error('Payment verification error');
          }
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
          contact: ''
        },
        theme: {
          color: '#1e3a5f'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err) {
      console.error('Payment error:', err);
      toast.error(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: '',
      description: 'Get started with basic features',
      features: [
        '5 AI queries per day',
        'Basic tax information',
        'Budget 2026 highlights',
        'Limited chat history'
      ],
      cta: 'Current Plan',
      popular: false,
      disabled: true
    },
    {
      id: 'pro_monthly',
      name: 'Pro Monthly',
      price: 0,
      originalPrice: 499,
      period: '/month',
      description: 'Perfect for individuals',
      features: [
        'Unlimited AI queries',
        'Priority response time',
        'Budget 2026 deep analysis',
        'Document upload & analysis',
        'Chat history (forever)',
        'Email support'
      ],
      cta: 'Get Pro Free',
      popular: true,
      disabled: false,
      badge: 'FREE'
    },
    {
      id: 'pro_yearly',
      name: 'Pro Yearly',
      price: 0,
      originalPrice: 3999,
      period: '/year',
      description: 'Best value for power users',
      features: [
        'Everything in Pro Monthly',
        'Priority support',
        'Early access to new features',
        'Exclusive webinars'
      ],
      cta: 'Get Pro Free',
      popular: false,
      disabled: false,
      badge: 'FREE'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-white">Pricing</h1>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button 
              variant="outline"
              size="sm"
              onClick={() => navigate('/chat')}
              className="border-slate-700 text-slate-300"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-sm mb-6">
            <Sparkles className="w-4 h-4" />
            🎉 Beta Launch - FREE for Limited Time!
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Simple, Transparent
            <span className="gradient-text"> Pricing</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Get unlimited access to India's smartest AI tax assistant. 
            Currently FREE during our beta launch period!
          </p>
        </motion.div>

        {/* Current Subscription Status */}
        {subscription && subscription.status === 'active' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8 p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-center"
          >
            <div className="flex items-center justify-center gap-2 text-green-400">
              <Crown className="w-5 h-5" />
              <span className="font-semibold">You're a Pro member!</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              Your subscription is active until {new Date(subscription.expires_at).toLocaleDateString('en-IN')}
            </p>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl p-6 ${
                plan.popular 
                  ? 'bg-gradient-to-b from-cyan-500/10 to-slate-800/50 border-2 border-cyan-500/50' 
                  : 'bg-slate-800/50 border border-slate-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-cyan-500 text-white text-xs font-bold">
                  MOST POPULAR
                </div>
              )}
              {plan.badge && (
                <div className="absolute -top-3 right-4 px-3 py-1 rounded-full bg-amber-500 text-white text-xs font-bold">
                  {plan.badge}
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm">{plan.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-green-400">₹{plan.price}</span>
                  {plan.originalPrice && (
                    <span className="text-xl text-slate-500 line-through">₹{plan.originalPrice}</span>
                  )}
                  <span className="text-slate-400">{plan.period}</span>
                </div>
                {plan.originalPrice && (
                  <p className="text-green-400 text-sm font-medium mt-1">🎉 Limited Time Offer!</p>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                    <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Button
                className={`w-full ${
                  plan.popular 
                    ? 'btn-primary text-white' 
                    : plan.disabled 
                    ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    : 'bg-slate-700 text-white hover:bg-slate-600'
                }`}
                onClick={() => !plan.disabled && handlePayment(plan.id)}
                disabled={plan.disabled || loading || (subscription?.status === 'active')}
              >
                {loading && selectedPlan === plan.id ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : subscription?.status === 'active' ? (
                  plan.id === 'free' ? 'Free Plan' : 'Already Pro'
                ) : (
                  <>
                    {plan.popular && <Sparkles className="w-4 h-4 mr-2" />}
                    {plan.cta}
                  </>
                )}
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Trust Badges */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-8 py-8 border-t border-slate-800"
        >
          <div className="flex items-center gap-2 text-slate-400">
            <Shield className="w-5 h-5 text-green-400" />
            <span className="text-sm">Secure Payments</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <IndianRupee className="w-5 h-5 text-amber-400" />
            <span className="text-sm">GST Invoice</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Clock className="w-5 h-5 text-cyan-400" />
            <span className="text-sm">Cancel Anytime</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Star className="w-5 h-5 text-amber-400" />
            <span className="text-sm">7-Day Refund</span>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: "What payment methods do you accept?",
                a: "We accept UPI, all major credit/debit cards, and net banking through Razorpay."
              },
              {
                q: "Can I cancel my subscription?",
                a: "Yes, you can cancel anytime. Your access will continue until the end of the billing period."
              },
              {
                q: "Is there a refund policy?",
                a: "Yes, we offer a 7-day money-back guarantee if you're not satisfied."
              },
              {
                q: "Will I get a GST invoice?",
                a: "Yes, GST-compliant invoices are automatically generated for all payments."
              }
            ].map((faq, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-slate-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default PricingPage;
